import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useCapabilities } from '@apis/capability/queries/use-capabilities';
import { useSystemCapabilities } from '@apis/capability/queries/use-system-capabilities';
import { useOrganizations } from '@apis/eloc/queries/use-organizations';
import { useLegacyCapabilities } from '@apis/rendall/queries/use-capabilities';
import { useSubscriptions } from '@apis/rendall/queries/use-subscriptions';
import { useUserInfo } from '@apis/rendall/queries/use-user-info';
import { useDataExplorerConfiguration } from '@packages/hooks/use-data-explorer-configuration/use-data-explorer-configuration';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { useFeatureFlags } from '@packages/platform/feature-flags/FeatureFlags';
import { useRendallOrganization } from 'ae-kernel/organizations/use-rendall-organization';

import { getNavData } from './routes.utils';

export function useNavData() {
  const { t } = useTranslation();

  const { selectedOrganization } = useOrganizationContext();
  const { currentOrganization, isPending: currentOrganizationIsPending } =
    useRendallOrganization();
  const { data: organizations } = useOrganizations();

  const { data: subscriptions, isPending: subscriptionsIsPending } =
    useSubscriptions();
  const { data: legacyCapabilities, isPending: legacyCapabilitiesIsPending } =
    useLegacyCapabilities();
  const { data: systemCapabilities, isPending: systemCapabilitiesIsPending } =
    useSystemCapabilities();
  const { data: capabilities, isPending: capabilitiesIsPending } =
    useCapabilities();

  const isPending =
    subscriptionsIsPending ||
    legacyCapabilitiesIsPending ||
    systemCapabilitiesIsPending ||
    capabilitiesIsPending ||
    currentOrganizationIsPending;

  const { data: user } = useUserInfo();

  const { featureFlags } = useFeatureFlags();

  const {
    dataExplorerConfiguration: { menuItem: dataExplorerMenuItem },
  } = useDataExplorerConfiguration();

  return useMemo(() => {
    if (isPending) return [];

    return getNavData({
      legacyCapabilities,
      capabilities,
      systemCapabilities,
      organization: currentOrganization,
      featureFlags,
      orgId: selectedOrganization.id,
      organizations,
      subscriptions,
      t,
      user,
      dataExplorerMenuItem,
    });
  }, [
    isPending,
    legacyCapabilities,
    capabilities,
    systemCapabilities,
    currentOrganization,
    featureFlags,
    selectedOrganization.id,
    organizations,
    subscriptions,
    t,
    user,
    dataExplorerMenuItem,
  ]);
}
